import type { Promotion, SignupBonusState } from '@promotion/types'
import type { DepositOffer } from '@payments/types'
import { computed, navigateTo, toValue, useApi, useCoreConfig, useDayjs, useFormat, useInfiniteQuery, useMutation, usePromotionClient, useQuery, useQueryClient, useRewardChecks, useNuxtApp, useUserInfo } from '#imports'
import type { MaybeRefOrGetter } from '#imports'
import type { ManipulateType } from 'dayjs'
import type { PromotionClient, UseMutationMutateOptions, UseQueryOverrideOptions } from '@core/types'
import { toActivatableReward, toActiveReward } from '@promotion/utils/playerOfferRewards'
import type { PlayerOfferRewardContext } from '@promotion/utils/playerOfferRewards'

/**
 * Composable for fetching and managing active and enqueued rewards.
 * Handles API calls, state, and event-driven refresh for rewards.
 * @returns State and methods for rewards fetching and management
 */
export function useRewardsClient() {
    const queryClient = useQueryClient()
    const api = useApi({ enableLegacySupport: true, service: 'monolith' })
    const dayjs = useDayjs()
    const { $i18n } = useNuxtApp()
    const { canUsePlayerFeatures } = useUserInfo()
    const { site } = useCoreConfig()
    const { client } = usePromotionClient()
    const { asMoney } = useFormat()

    // The account history intervals are monolith lookback strings ("3 months"); PS takes an absolute `from` instead.
    function lookbackStart(interval: string): string {
        const match = /^(\d+) ([a-z]+)$/.exec(interval)

        if (!match) throw new Error(`Cannot turn the history interval "${interval}" into a timestamp`)

        return dayjs().subtract(Number(match[1]), match[2] as ManipulateType).toISOString()
    }

    const rewardContext: PlayerOfferRewardContext = {
        requirementMessage: (type, amount) => $i18n.t(`promotions.requirementMessage.${type.toLowerCase()}`, {
            amount: asMoney(Number(amount), { decimals: 0 }),
        }),
    }

    // site.brand may be the 'unknown' fallback; cast to the nominal Brand enum.
    const brandQuery = { query: { brand: site.brand as PromotionClient.Brand } }

    function useGetActiveRewards(options: UseQueryOverrideOptions = {}) {
        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/v1/public/player-offers/active', { signal, params: brandQuery })

            return (data ?? []).map(item => toActiveReward(item, rewardContext)).sort(
                (a: Promotion, b: Promotion) => dayjs(a.expires_at).diff(dayjs(b.expires_at))
            )
        }, {
            lazy: true,
            server: false,
            ...options,
            key: 'promotions:rewards:active',
            enabled: computed(() => canUsePlayerFeatures.value && toValue(options.enabled ?? true)),
        })
    }

    function useGetEnqueuedRewards(options: UseQueryOverrideOptions = {}) {
        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/v1/public/player-offers/activatable', { signal, params: brandQuery })

            return (data ?? []).map(item => toActivatableReward(item, rewardContext)).sort(
                (a: Promotion, b: Promotion) => dayjs(a.expires_at).diff(b.expires_at)
            )
        }, {
            server: false,
            lazy: true,
            ...options,
            key: 'promotions:rewards:enqueued',
            enabled: computed(() => canUsePlayerFeatures.value && toValue(options.enabled ?? true)),
        })
    }

    function useCancelReward(options: UseMutationMutateOptions = {}) {
        return useMutation(async (reward: Promotion) => {
            if (!reward?.cancel_url) throw new Error(`Reward ${reward?.id} cannot be cancelled`)

            return api(reward.cancel_url, { method: 'PUT' })
        }, {
            mode: 'leading',
            ...options,
            async onSuccess(...args) {
                if (options.onSuccess) {
                    await options.onSuccess(...args)
                }

                await queryClient.invalidateQueries({
                    key: 'promotions'
                })
            }
        })
    }

    function useActivateReward(options: UseMutationMutateOptions = {}) {
        const { REQUIREMENT_TYPE_PAYMENT } = useRewardChecks()

        const mutation = useMutation(async (reward: Promotion) => {
            if (!reward) return

            if (!reward.activate_url) throw new Error(`Reward ${reward.id} cannot be activated`)

            return api(reward.activate_url, { method: 'PUT' })
        }, {
            mode: 'leading',
            ...options,
            async onSuccess(data, body) {
                if (options.onSuccess) {
                    await options.onSuccess(data, body)
                }

                // Redirect to payment after activation if response includes hasPaymentRequirement
                if (data?.hasPaymentRequirement) {
                    await navigateTo({ path: $i18n.t('pages.deposit'), query: { claimingBonus: 'true' } })
                    return
                }

                // Redirect to payment after activation if the reward has a payment requirement
                if (body?.requirement_type === REQUIREMENT_TYPE_PAYMENT) {
                    await navigateTo({ path: $i18n.t('pages.deposit'), query: { claimingBonus: 'true' } })
                    return
                }

                await queryClient.invalidateQueries({
                    key: 'promotions'
                })
            }
        })

        return mutation
    }

    function useGetBonusHistory(interval: MaybeRefOrGetter<string>, options: UseQueryOverrideOptions = {}) {
        const from = computed(() => lookbackStart(toValue(interval)))

        return useInfiniteQuery(async ({ pageParam, signal }) => {
            const { data } = await client.GET('/v1/public/bonus-history', {
                signal,
                params: { query: { ...brandQuery.query, page: pageParam, perPage: 10, from: from.value } }
            })

            return data
        }, {
            ...options,
            key: () => `get-reward:bonus:${toValue(interval).replace(/ /g, '-')}`,
            initialPageParam: 1,
            getNextPageParam({ currentPage }) {
                if (!currentPage?.data) return

                return currentPage.data.currentPage < currentPage.data.lastPage ? currentPage.data.currentPage + 1 : undefined
            },
            getPreviousPageParam({ currentPage }) {
                if (!currentPage?.data) return

                return currentPage.data.currentPage > 1 ? currentPage.data.currentPage - 1 : undefined
            },
        })
    }

    function useGetPendingOffersWithPaymentRequirements(options: UseQueryOverrideOptions & {
        hasBonus: (offer: DepositOffer) => boolean
    }) {
        const { hasBonus, ...queryOptions } = options

        const query = useQuery<DepositOffer | null>(
            async ({ signal }) => {
                const { data } = await client.GET('/v1/public/player-offer-requirements/pending-payment', { signal })

                // undefined (PS's 204) reads as not loaded downstream.
                return data ?? null
            },
            {
                server: false,
                lazy: true,
                ...queryOptions,
                key: 'promotions:account-completion:signup-bonus',
                enabled: computed(() => canUsePlayerFeatures.value && toValue(queryOptions.enabled ?? true)),
            }
        )

        const hasSignupBonus = computed<SignupBonusState>(() => {
            if (!toValue(options.enabled) || !canUsePlayerFeatures.value) return false
            if (query.status.value === 'error') return false
            if (query.status.value !== 'success') return undefined

            const offer = query.data.value
            if (!offer) return false

            return hasBonus(offer)
        })

        return query.enrich({ hasSignupBonus })
    }

    return {
        useGetActiveRewards,
        useGetEnqueuedRewards,
        useCancelReward,
        useActivateReward,
        useGetBonusHistory,
        useGetPendingOffersWithPaymentRequirements
    }
}
